import { browserStorageKey } from "./browserStorageScope.js";

const DEFAULT_RENDER_COLOR = "var(--green)";

const RENDER_COLOR_TOKENS = new Set([
    "--bg",
    "--panel",
    "--panel-soft",
    "--control-bg",
    "--ink",
    "--muted",
    "--muted-soft",
    "--green",
    "--green-soft",
    "--red",
    "--line",
    "--line-soft",
    "--line-strong",
    "--plot-bg",
    "--plot-ink",
    "--plot-ink-soft",
    "--rail-bg",
    "--rail-ink",
    "--eyebrow",
    "--diagram-dot",
]);

const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR_RE = /^(rgb|rgba)\(\s*(-?(?:\d+(?:\.\d+)?|\.\d+))\s*,\s*(-?(?:\d+(?:\.\d+)?|\.\d+))\s*,\s*(-?(?:\d+(?:\.\d+)?|\.\d+))(?:\s*,\s*(-?(?:\d+(?:\.\d+)?|\.\d+)))?\s*\)$/i;
const THEME_COLOR_RE = /^var\(\s*(--[\w-]+)(?:\s*,\s*(.+))?\s*\)$/i;
const CONTROL_CHARACTER_RE = /[\u0000-\u001f\u007f]/;

export const LEGACY_REMEMBERED_RENDER_HOSTS_KEY =
    "originDashboard.rememberedRenderLinkHosts.v1";
const LEGACY_REMEMBERED_RENDER_ORIGINS_KEY =
    "originDashboard.rememberedRenderLinkOrigins.v2";
export const REMEMBERED_RENDER_HOSTS_KEY = LEGACY_REMEMBERED_RENDER_HOSTS_KEY;
export const REMEMBERED_RENDER_ORIGINS_KEY =
    "originDashboard.rememberedRenderLinkOrigins.v3";

function normalizeNumericColor(value) {
    const match = RGB_COLOR_RE.exec(value);
    if (!match) return null;
    const [, fn, redText, greenText, blueText, alphaText] = match;
    const channels = [redText, greenText, blueText].map(Number);
    if (channels.some((channel) => !Number.isFinite(channel) || channel < 0 || channel > 255)) {
        return null;
    }
    const wantsAlpha = fn.toLowerCase() === "rgba";
    if (wantsAlpha !== (alphaText != null)) return null;
    const alpha = alphaText == null ? null : Number(alphaText);
    if (alpha != null && (!Number.isFinite(alpha) || alpha < 0 || alpha > 1)) {
        return null;
    }
    const normalizedChannels = channels.map((channel) => String(channel));
    return wantsAlpha
        ? `rgba(${normalizedChannels.join(", ")}, ${alpha})`
        : `rgb(${normalizedChannels.join(", ")})`;
}

function normalizeLiteralColor(value) {
    if (HEX_COLOR_RE.test(value)) return value.toLowerCase();
    return normalizeNumericColor(value);
}

function normalizeRenderColor(value) {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    const literal = normalizeLiteralColor(trimmed);
    if (literal) return literal;

    const theme = THEME_COLOR_RE.exec(trimmed);
    if (!theme || !RENDER_COLOR_TOKENS.has(theme[1])) return null;
    const fallback = theme[2] == null
        ? null
        : normalizeLiteralColor(theme[2].trim());
    if (theme[2] != null && !fallback) return null;
    return fallback
        ? `var(${theme[1]}, ${fallback})`
        : `var(${theme[1]})`;
}

export function sanitizeRenderColor(value, fallback = DEFAULT_RENDER_COLOR) {
    return normalizeRenderColor(value)
        || normalizeRenderColor(fallback)
        || DEFAULT_RENDER_COLOR;
}

export function sanitizeRenderHref(value) {
    if (typeof value !== "string") return null;
    if (CONTROL_CHARACTER_RE.test(value)) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
        const url = new URL(trimmed);
        if (!["http:", "https:", "mailto:"].includes(url.protocol)) return null;
        if ((url.protocol === "http:" || url.protocol === "https:") && !url.host) {
            return null;
        }
        if (url.protocol === "http:" && !isLoopbackHostname(url.hostname)) return null;
        return url.href;
    } catch {
        return null;
    }
}

export function sanitizeLocalDownloadHref(value) {
    if (typeof value !== "string") return null;
    if (value.startsWith("blob:")) return value;
    if (/^data:image\/png;base64,[a-z0-9+/=\r\n]+$/i.test(value)) return value;
    return null;
}

function isLoopbackHostname(value) {
    const hostname = String(value || "").toLowerCase().replace(/^\[|\]$/g, "");
    if (hostname === "localhost" || hostname.endsWith(".localhost") || hostname === "::1") {
        return true;
    }
    const octets = hostname.split(".");
    return octets.length === 4
        && octets[0] === "127"
        && octets.every((octet) =>
            /^\d{1,3}$/.test(octet) && Number(octet) <= 255);
}

function normalizeExactOrigin(value) {
    if (typeof value !== "string") return null;
    if (CONTROL_CHARACTER_RE.test(value)) return null;
    const trimmed = value.trim();
    if (!trimmed || trimmed.includes("*")) return null;
    try {
        const url = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
        if (
            !["http:", "https:"].includes(url.protocol)
            || (url.protocol === "http:" && !isLoopbackHostname(url.hostname))
            || url.username
            || url.password
            || url.pathname !== "/"
            || url.search
            || url.hash
        ) {
            return null;
        }
        return url.origin;
    } catch {
        return null;
    }
}

export function parseRenderDocumentationOrigins(value) {
    const candidates = Array.isArray(value)
        ? value
        : String(value || "").split(",");
    return [...new Set(candidates.map(normalizeExactOrigin).filter(Boolean))];
}

export function getConfiguredRenderDocumentationOrigins() {
    const value =
        typeof import.meta !== "undefined" && import.meta.env
            ? import.meta.env.VITE_ORIGIN_DOCUMENTATION_HOSTS
            : "";
    return parseRenderDocumentationOrigins(value);
}

export function isTrustedRenderDestination(
    value,
    {
        currentOrigin =
            typeof globalThis.location !== "undefined"
                ? globalThis.location.origin
                : "",
        documentationHosts = getConfiguredRenderDocumentationOrigins(),
    } = {},
) {
    const href = sanitizeRenderHref(value);
    if (!href) return false;
    const url = new URL(href);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    try {
        if (currentOrigin && url.origin === new URL(currentOrigin).origin) {
            return true;
        }
    } catch {}
    const trustedOrigins = new Set(parseRenderDocumentationOrigins(documentationHosts));
    return trustedOrigins.has(url.origin);
}

function renderOriginStorage() {
    try {
        return globalThis.localStorage || null;
    } catch {
        return null;
    }
}

export function loadRememberedRenderOrigins() {
    const storage = renderOriginStorage();
    if (!storage) return [];
    try {
        storage.removeItem(LEGACY_REMEMBERED_RENDER_HOSTS_KEY);
        storage.removeItem(LEGACY_REMEMBERED_RENDER_ORIGINS_KEY);
        const key = browserStorageKey(REMEMBERED_RENDER_ORIGINS_KEY);
        if (!key) return [];
        const parsed = JSON.parse(storage.getItem(key) || "[]");
        return parseRenderDocumentationOrigins(Array.isArray(parsed) ? parsed : []);
    } catch {
        return [];
    }
}

export function isRememberedRenderOrigin(origin) {
    const normalized = normalizeExactOrigin(origin);
    return normalized != null && loadRememberedRenderOrigins().includes(normalized);
}

export function rememberRenderOrigin(origin) {
    const normalized = normalizeExactOrigin(origin);
    const storage = renderOriginStorage();
    const key = browserStorageKey(REMEMBERED_RENDER_ORIGINS_KEY);
    if (!normalized || !storage || !key) return false;
    try {
        const origins = new Set(loadRememberedRenderOrigins());
        origins.add(normalized);
        storage.setItem(
            key,
            JSON.stringify([...origins].sort()),
        );
        return true;
    } catch {
        return false;
    }
}

export function clearRememberedRenderOrigins() {
    try {
        const storage = renderOriginStorage();
        const key = browserStorageKey(REMEMBERED_RENDER_ORIGINS_KEY);
        if (key) storage?.removeItem(key);
        storage?.removeItem(LEGACY_REMEMBERED_RENDER_ORIGINS_KEY);
        storage?.removeItem(LEGACY_REMEMBERED_RENDER_HOSTS_KEY);
    } catch {}
}

export const parseRenderDocumentationHosts = parseRenderDocumentationOrigins;
export const getConfiguredRenderDocumentationHosts = getConfiguredRenderDocumentationOrigins;
export const loadRememberedRenderHosts = loadRememberedRenderOrigins;
export const isRememberedRenderHost = isRememberedRenderOrigin;
export const rememberRenderHost = rememberRenderOrigin;
export const clearRememberedRenderHosts = clearRememberedRenderOrigins;
